import { tracked } from '@glimmer/tracking';

import type { BoardPreviewMap } from 'webapp-types';

// valid URL params but not part of preview params - needs to be excluded
const noPreviewKeys = [
	'i', // iframe - when embedding
	'itemId', // current visible board or report slide id
	'present', // reports - presentation view
	'utm_campaign', // from emails
	'utm_medium', // from emails
	'utm_source', // from emails
	'utm_content', // from emails
];

export default class PreviewUrlManager {
	@tracked urlSearchParams = new URLSearchParams(window.location.search);

	@tracked rootUrl = '';

	@tracked _previewMap: BoardPreviewMap = {};

	constructor(rootUrl: string) {
		this.rootUrl = rootUrl;
		this.setInitialPreviewMapFromUrl();
		this.updatePresentViewClass();
	}

	get previewMap() {
		return this._previewMap;
	}

	set previewMap(value: BoardPreviewMap) {
		this._previewMap = value;
		this.updatePathFromPreviewMap(value);
	}

	get itemIdParam() {
		return this.urlSearchParams.get('itemId') || '';
	}

	set itemIdParam(itemId: string) {
		this.urlSearchParams.set('itemId', itemId);
		this.updateHistoryState();
	}

	get isPresentView() {
		return this.urlSearchParams.get('present') === 'true';
	}

	set isPresentView(isPresent: boolean) {
		this.urlSearchParams.set('present', String(isPresent));
		this.updatePresentViewClass();
		this.updateHistoryState();
	}

	get isInIframe(): boolean {
		return this.urlSearchParams?.has('i');
	}

	updateHistoryState(): void {
		const currentPath = window.location.search.replace('?', ''),
			path = this.urlSearchParams.toString().replace('i=', 'i'); // Prevent appending = after i (without value)

		if (currentPath !== path) {
			this.urlSearchParams = new URLSearchParams(path);
			window.history.replaceState(null, '', `${this.rootUrl}?${path}`);
		}
	}

	private setInitialPreviewMapFromUrl() {
		const previewMap: BoardPreviewMap = {};
		this.urlSearchParams.forEach((value, key) => {
			if (!noPreviewKeys.includes(key)) {
				previewMap[key] = value;
			}
		});
		this.previewMap = previewMap;
	}

	private updatePathFromPreviewMap(previewMap: BoardPreviewMap): void {
		const previewKeys = Object.keys(previewMap);
		// Removing old preview keys with values
		for (const key of this.urlSearchParams.keys()) {
			if (!previewKeys.includes(key) && !noPreviewKeys.includes(key)) {
				this.urlSearchParams.delete(key);
			}
		}

		// Adding or resetting keys with values
		previewKeys.forEach((itemId) => {
			this.urlSearchParams.set(itemId, previewMap[itemId]!);
		});

		this.updateHistoryState();
	}

	private updatePresentViewClass() {
		document.body.classList.toggle('present-view', this.isPresentView);
	}
}
